import { useEffect, useState, useCallback, useRef } from 'react'
import PlayableForexChart from '../../components-legacy/PlayableForexChart'
import { server } from './../chat/Server'
import { DATAFEED_API } from '../../config/index.js'
import { getInitialCandlesForTimeframe, getInitialLoadPeriod } from '../../utils/chartUtils'

export default function StrategyBacktestVisual({ strategyId, backtestId, initialMode = 'normal', autoPlay = false }) {
  const [selectedSymbol, setSelectedSymbol] = useState('')
  const [selectedTimeframe, setSelectedTimeframe] = useState('')
  const [candleData, setCandleData] = useState([])
  const [orders, setOrders] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [backtestStartTimestamp, setBacktestStartTimestamp] = useState(null)
  const [backtestEndTimestamp, setBacktestEndTimestamp] = useState(null)
  const [viewMode, setViewMode] = useState(initialMode) 
  const [isPlaying, setIsPlaying] = useState(false)
  const [playSpeed, setPlaySpeed] = useState(2) 
  const [visibleCandles, setVisibleCandles] = useState([])
  const [playWindowSize, setPlayWindowSize] = useState(0)
  const [currentPlayIndex, setCurrentPlayIndex] = useState(0)
  const [pausedViewportRange, setPausedViewportRange] = useState(null)
  const playIntervalRef = useRef(null)

  const advanceCandles = useCallback(() => {
    setCurrentPlayIndex(prevIndex => {
      const newIndex = Math.min(prevIndex + playSpeed, candleData.length)
      
      if (newIndex >= candleData.length) {
        setIsPlaying(false)
        clearInterval(playIntervalRef.current)
        
        setVisibleCandles(candleData.slice(0, candleData.length))
        
        const windowStart = Math.max(0, candleData.length - playWindowSize)
        const windowEnd = candleData.length - 1
        setPausedViewportRange({ from: windowStart, to: windowEnd })
        
        return candleData.length
      }
      
      setVisibleCandles(() => {
        const windowStart = Math.max(0, newIndex - playWindowSize)
        const windowEnd = newIndex
        return candleData.slice(windowStart, windowEnd)
      })
      
      return newIndex
    })
  }, [candleData, playSpeed, playWindowSize])

  const handlePlayPause = useCallback(() => {
    if (viewMode === 'normal') {
      setViewMode('play')
      setPlaySpeed(2)
      setIsPlaying(true)
      
      if (candleData.length > 0) {
        const windowSize = getInitialCandlesForTimeframe(selectedTimeframe, candleData.length)
        setPlayWindowSize(windowSize)
        
        const startIndex = currentPlayIndex > 0 ? currentPlayIndex : windowSize
        setCurrentPlayIndex(startIndex)
        
        const windowStart = Math.max(0, startIndex - windowSize)
        const windowEnd = startIndex
        setVisibleCandles(candleData.slice(windowStart, windowEnd))
      }
    } else {
      setIsPlaying(prev => {
        const newIsPlaying = !prev
        
        if (newIsPlaying) {
          if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current)
          }
          
          setPausedViewportRange(null)
          
          playIntervalRef.current = setInterval(() => {
            advanceCandles()
          }, 500 / playSpeed)
        } else {
          if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current)
            playIntervalRef.current = null
          }
          
          setVisibleCandles(candleData.slice(0, currentPlayIndex))
          
          const windowStart = Math.max(0, currentPlayIndex - playWindowSize)
          const windowEnd = currentPlayIndex - 1
          setPausedViewportRange({ from: windowStart, to: windowEnd })
        }
        
        return newIsPlaying
      })
    }
  }, [viewMode, candleData, playSpeed, advanceCandles, selectedTimeframe, currentPlayIndex, playWindowSize])

  const handleFastForward = useCallback(() => {
    setPlaySpeed(prevSpeed => {
      const newSpeed = prevSpeed === 2 ? 4 : 2
      
      if (isPlaying && playIntervalRef.current) {
        clearInterval(playIntervalRef.current)
        playIntervalRef.current = setInterval(() => {
          advanceCandles()
        }, 500 / newSpeed)
      }
      
      return newSpeed
    })
  }, [isPlaying, advanceCandles])

  const fetchInitialCandleData = useCallback(async (symbol, timeframe, startTimestamp, endTimestamp) => {
    if (!symbol || !timeframe || !startTimestamp || !endTimestamp) {
      setError('Missing required parameters for fetching candle data')
      setIsLoading(false)
      return
    }

    try {
      const startTime = typeof startTimestamp === 'number' ? startTimestamp : Math.floor(new Date(startTimestamp).getTime() / 1000)
      const endTime = typeof endTimestamp === 'number' ? endTimestamp : Math.floor(new Date(endTimestamp + 'T23:59:59').getTime() / 1000)

      setBacktestStartTimestamp(startTime)
      setBacktestEndTimestamp(endTime)

      const initialPeriod = getInitialLoadPeriod(startTime)

      const url = `${DATAFEED_API}/datafeed/candles/?symbol=${symbol}&timeframe=${timeframe}&start_timestamp=${initialPeriod.start}&end_timestamp=${initialPeriod.end}`

      const response = await fetch(url)

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.detail || 'Failed to fetch candle data'
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()

      setCandleData(data)

      if (data.length === 0) {
        setError('No data available for the selected period')
      }
    } catch (err) {
      console.error('Error fetching candle data:', err)
      setError(`Failed to load chart data`)
      setCandleData([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loadMoreCandleData = useCallback(async (symbol, timeframe, startTimestamp, endTimestamp) => {

    try {
      const url = `${DATAFEED_API}/datafeed/candles/?symbol=${symbol}&timeframe=${timeframe}&start_timestamp=${startTimestamp}&end_timestamp=${endTimestamp}`

      const response = await fetch(url)

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.detail || 'Failed to fetch additional candle data'
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const newData = await response.json()

      if (newData.length > 0) {
        setCandleData(prevData => {
          const allData = [...prevData, ...newData]
          const uniqueData = Array.from(
            new Map(allData.map(candle => [candle.time, candle])).values()
          ).sort((a, b) => a.time - b.time)
          
          return uniqueData
        })
      }
    } catch (err) {
      console.error('Error loading additional candle data:', err)
    }
  }, [])

  const fetchBacktestDetails = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const details = await server.api('strategy/backtest/details', {
        strId: strategyId,
        backtestId: backtestId
      })

      const parsedDetails = typeof details === 'string' ? JSON.parse(details) : details

      if (parsedDetails && parsedDetails.orders && Array.isArray(parsedDetails.orders)) {
        setOrders(parsedDetails.orders)
      } else {
        setOrders([])
      }

      if (parsedDetails && parsedDetails.symbol && parsedDetails.period) {
        const symbol = parsedDetails.symbol
        const timeframe = parsedDetails.period  //DEV: timeframe mark
        let dateStart = parsedDetails.dateStart
        let dateEnd = parsedDetails.dateEnd

        setSelectedSymbol(symbol || '')
        setSelectedTimeframe(timeframe || '')

        const startTimestamp = typeof dateStart === 'number' ? dateStart : Math.floor(new Date(dateStart).getTime() / 1000)
        const endTimestamp = typeof dateEnd === 'number' ? dateEnd : Math.floor(new Date(dateEnd).getTime() / 1000)

        if (symbol && timeframe && dateStart && dateEnd) {
          fetchInitialCandleData(symbol, timeframe, startTimestamp, endTimestamp)
        } else {
          setError('Incomplete backtest parameters')
          setIsLoading(false)
        }
      } else {
        console.error('Invalid backtest details format:', parsedDetails)
        setError('Invalid backtest details format')
        setIsLoading(false)
      }
    } catch (err) {
      console.error('Error fetching backtest details:', err)
      setError('Failed to load backtest details. Please try again later.')
      setIsLoading(false)
    }
  }, [strategyId, backtestId, fetchInitialCandleData])

  useEffect(() => {
    if (initialMode === 'play' && candleData.length > 0 && visibleCandles.length === 0) {
      const windowSize = getInitialCandlesForTimeframe(selectedTimeframe, candleData.length)
      setPlayWindowSize(windowSize)
      
      const startIndex = windowSize
      setCurrentPlayIndex(startIndex)
      
      const windowStart = Math.max(0, startIndex - windowSize)
      const windowEnd = startIndex
      setVisibleCandles(candleData.slice(windowStart, windowEnd))
    }
  }, [initialMode, candleData, selectedTimeframe, visibleCandles.length])

  useEffect(() => {
    if (viewMode === 'play' && candleData.length > 0 && isPlaying) {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current)
      }
      
      playIntervalRef.current = setInterval(() => {
        advanceCandles()
      }, 500 / playSpeed)
    }
    
    return () => {
      if (viewMode !== 'play' && playIntervalRef.current) {
        clearInterval(playIntervalRef.current)
        playIntervalRef.current = null
      }
    }
  }, [viewMode, candleData, isPlaying, playSpeed, advanceCandles])

  useEffect(() => {
    if (strategyId && backtestId) {
      fetchBacktestDetails()
    } else {
      setError('Missing strategy ID or backtest ID')
      setIsLoading(false)
    }
    
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current)
        playIntervalRef.current = null
      }
    }
  }, [strategyId, backtestId, fetchBacktestDetails])

  useEffect(() => {
    if (autoPlay && viewMode === 'play' && candleData.length > 0 && !isPlaying) {
      handlePlayPause()
    }
  }, [autoPlay, viewMode, candleData, isPlaying, handlePlayPause])

  return (
    <div className="w-full py-3">
      {error && <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3 rounded border-l-4 border-red-700 dark:border-red-500 mb-5 text-sm m-4">{error}</div>}
      
      {!isLoading && candleData.length > 0 && viewMode === 'play' && (
        <div className="flex flex-col mb-2">
          <div className="flex gap-2.5 items-center px-4 py-1">
            <button
              className="px-5 py-2.5 bg-base-200 dark:bg-base-dark-200 border border-base-300 dark:border-base-dark-300 rounded transition-all duration-200 font-medium min-w-[120px] hover:bg-base-300 dark:hover:bg-base-dark-300"
              onClick={handlePlayPause}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '❚❚' : '▶'} {isPlaying ? 'Pause' : 'Play'}
            </button>

            <button
              className={`px-5 py-2.5 bg-base-200 dark:bg-base-dark-200 border border-base-300 dark:border-base-dark-300 rounded transition-all duration-200 font-medium min-w-[120px] hover:bg-base-300 dark:hover:bg-base-dark-300 ${playSpeed === 4 ? 'text-blue-600 dark:text-blue-400' : ''}`}
              onClick={handleFastForward}
              title={`Fast Forward`}
            >
              ▶▶
            </button>
          </div>
        </div>
      )}

      <div>
        {isLoading ? (
          <div className="flex items-center justify-center h-[500px] text-base-500 dark:text-base-dark-500 text-base">Loading chart data...</div>
        ) : candleData.length > 0 ? (
          <PlayableForexChart
            data={candleData}
            visibleData={visibleCandles}
            orders={orders}
            symbol={selectedSymbol}
            timeframe={selectedTimeframe}
            height={500}
            mode={viewMode}
            isPlaying={isPlaying}
            pausedViewportRange={pausedViewportRange}
            enableDynamicLoading={true}
            backtestStartTimestamp={backtestStartTimestamp}
            backtestEndTimestamp={backtestEndTimestamp}
            onLoadMoreData={loadMoreCandleData}
          />
        ) : (
          <div className="no-data m-4">No chart data available for this backtest</div>
        )}
      </div>
    </div>
  )
}
